/**
 * O quadro da peça aberta na mesa, em leitura — ou o caminho de volta à preliminar (E78.F4). O
 * mesmo componente da pré-análise: repetir o desenho faria as duas telas divergirem.
 *
 * E184.F4.T01 (GUS-145 item 3): no PDF, "Abrir o original" abre o arquivo numa aba nova, para
 * copiar o texto; a foto fica como está.
 */
import type { AxiosInstance } from 'axios';

import { QuadroPorEixos } from '@/components/ExameEletronico/QuadroPorEixos';
import { AbrirOriginal } from '@/components/FichaDaLeitura/AbrirOriginal';
import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { tituloDaPeca } from '@/lib/exame_eletronico_peca';

const t = ptBR.mesaEletronica;

const PDF = /\.pdf$/i;

export function QuadroDaPeca({
  client,
  caseId,
  peca,
  indice,
  onAbrirPreliminar,
}: {
  client: AxiosInstance;
  caseId: string;
  peca: ExameEletronicoCcbData;
  indice: number;
  onAbrirPreliminar: () => void;
}): JSX.Element {
  const resultado = peca.exame?.resultado ?? null;
  const titulo = tituloDaPeca(peca, indice);
  return (
    <Blueprint as="article" className="space-y-sc-4 p-sc-6" data-testid="mesa-peca-aberta">
      <h2 className="text-display-sm text-fg">{titulo}</h2>
      {PDF.test(peca.original_filename) ? (
        <AbrirOriginal
          client={client}
          caseId={caseId}
          documentoId={peca.source_document_id}
          nome={titulo}
        />
      ) : null}
      {resultado === null ? (
        <div data-testid="mesa-peca-sem-exame">
          <p className="text-body-sm text-fg-muted">{t.semExame}</p>
          <Button type="button" variant="secondary" className="mt-sc-3" onClick={onAbrirPreliminar}>
            {t.irParaPreliminar}
          </Button>
        </div>
      ) : (
        <QuadroPorEixos
          resultado={resultado}
          tendencia={peca.exame?.tendencia ?? null}
          caseId={peca.exame?.case_id}
        />
      )}
    </Blueprint>
  );
}
